import { useCallback, useMemo } from 'react'

import { StackSeparator, VStack } from '@chakra-ui/react'
import { EDITOR_TOOL_BUTTON_SIZE } from '@editor/config/constants'
import ToolbarButton from '@editor/pages/projects/editor/toolbar/components/ToolbarButton'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import mixpanel from 'mixpanel-browser'

import { BlueprintEditorTool, BlueprintToolbarCategory } from '../definitions'
import { setSelectedTool } from '../store/blueprintEditor'
import { Tools } from '../tools/setup'

const Toolbar = () => {
  // Store
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector((state) => state.user.userProfile)

  const selectedTool = useAppSelector((state) => state.blueprint.editor.selectedTool)
  const mode = useAppSelector((state) => state.blueprint.editor.mode)
  const isCreatingBlueprint = useAppSelector((state) => state.blueprint.editor.isCreatingBlueprint)
  const isAllowedToModify = useAppSelector((state) => state.blueprint.editor.isAllowedToModify)

  // Derived state
  const isDisabledMode = useCallback(
    (tool: BlueprintEditorTool) => tool.toolbar?.modes && !tool.toolbar.modes.includes(mode),
    [mode],
  )

  // Group tools by category
  const groupedTools = useMemo(() => {
    const categories = Tools.reduce<{ category: BlueprintToolbarCategory; tools: BlueprintEditorTool[] }[]>(
      (collection, tool) => {
        const category = tool.toolbar?.category || BlueprintToolbarCategory.None
        const groupIndex = category as number
        const group = collection[groupIndex] || { category, tools: [] }

        if (tool.toolbar && tool.authCheck(userProfile, isAllowedToModify)) {
          group.tools.push(tool)
        }

        if (!collection[groupIndex]) {
          collection[groupIndex] = group
        }

        return collection
      },
      [],
    )

    return categories
  }, [userProfile, isAllowedToModify])

  return (
    <VStack left={2} overflow="hidden" position="absolute" top={2} w={EDITOR_TOOL_BUTTON_SIZE} gap={3}>
      {groupedTools.map((group) =>
        group.tools.length ? (
          <VStack
            key={group.category}
            data-testid={`ToolbarGroup-${Object.values(BlueprintToolbarCategory)[group.category]}`}
            backgroundColor="gray2.800"
            overflow="hidden"
            borderRadius="md"
            separator={<StackSeparator borderColor="whiteAlpha.200" />}
            gap={0}
            w={EDITOR_TOOL_BUTTON_SIZE}
          >
            {group.tools.map((tool) => (
              <ToolbarButton
                key={tool.key}
                icon={tool.toolbar!.icon}
                label={tool.toolbar!.label}
                toolType={tool.key}
                buttonSize={EDITOR_TOOL_BUTTON_SIZE}
                selectedTool={selectedTool}
                changeTool={() => {
                  mixpanel.track('Blueprint Tool Selected', {
                    Tool: tool.key,
                    Category:
                      Object.values(BlueprintToolbarCategory)[tool.toolbar?.category ?? BlueprintToolbarCategory.None],
                  })
                  dispatch(setSelectedTool(tool.key))
                }}
                disabled={
                  !tool.authCheck(userProfile, isAllowedToModify) || isDisabledMode(tool) || isCreatingBlueprint
                }
                variants={tool.toolbar?.variants?.reduce<
                  {
                    icon: React.ReactElement
                    toolType: string
                    label: string
                  }[]
                >((collection, variant) => {
                  if (variant.toolbar && variant.authCheck(userProfile, isAllowedToModify)) {
                    collection.push({
                      toolType: variant.key,
                      icon: variant.toolbar.icon,
                      label: variant.toolbar.label,
                    })
                  }

                  return collection
                }, [])}
              />
            ))}
          </VStack>
        ) : null,
      )}
    </VStack>
  )
}

export default Toolbar
